import React from 'react';
import { HandleJumpToSlideParamsTypes, UseCarouselAnimationReturnTypes } from './hooksTypes';

const useCarouselAnimation = ({
  overrideMaxIndex,
}: {
  overrideMaxIndex?: number;
}): UseCarouselAnimationReturnTypes => {
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const carouselItemsRef = React.useRef<HTMLDivElement[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [maxIndex, setMaxIndex] = React.useState(overrideMaxIndex || 0);

  const calculateShiftAmount = React.useCallback((index: number) => {
    if (carouselItemsRef.current.length === 0) return 0;

    const computedStyle = window.getComputedStyle(carouselItemsRef.current[0]);
    const cssCarouselMarginRight = computedStyle.getPropertyValue('margin-right');
    const cssCarouselWidth = computedStyle.getPropertyValue('width');

    const itemCarouselWidth = parseFloat(cssCarouselWidth);
    const itemRightMargin = parseFloat(cssCarouselMarginRight);

    return (itemCarouselWidth + itemRightMargin) * index;
  }, []);

  const animateCarousel = React.useCallback((shiftAmount: number) => {
    if (!carouselRef.current) return;

    carouselRef.current.style.transition = 'transform 0.58s ease-out';
    carouselRef.current.style.transform = `translate3d(${-shiftAmount}px, 0, 0)`;
  }, []);

  const calculateMaxIndex = React.useCallback(() => {
    if (carouselItemsRef.current.length === 0 || !containerRef.current) {
      setMaxIndex(0);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const adjustedWidth = containerWidth + (window.innerWidth - containerWidth) / 2;

    const totalItemsWidth = carouselItemsRef.current.reduce((acc, item) => {
      const computedStyle = window.getComputedStyle(item);
      const itemWidth = parseFloat(computedStyle.getPropertyValue('width'));
      const itemMarginRight = parseFloat(computedStyle.getPropertyValue('margin-right'));
      return acc + itemWidth + itemMarginRight;
    }, 0);

    const itemsVisibleInViewport = Math.floor(
      adjustedWidth / (totalItemsWidth / carouselItemsRef.current.length)
    );

    const maxIndexCalculated = Math.max(
      0,
      carouselItemsRef.current.length - itemsVisibleInViewport
    );

    setMaxIndex(maxIndexCalculated);
  }, []);

  const handleAddCarouselItemsToRef = (el: HTMLDivElement) => {
    if (el && !carouselItemsRef.current.includes(el)) {
      carouselItemsRef.current.push(el);

      if (!overrideMaxIndex) {
        calculateMaxIndex();
      }
    }
  };

  const handleShowNextItem = () => {
    if (maxIndex <= 0) return;

    const newIndex = Math.min(activeIndex + 1, maxIndex);
    setActiveIndex(newIndex);
    animateCarousel(calculateShiftAmount(newIndex));
  };

  const handleShowPreviousItem = () => {
    if (maxIndex <= 0) return;

    const newIndex = Math.max(activeIndex - 1, 0);
    setActiveIndex(newIndex);
    animateCarousel(calculateShiftAmount(newIndex));
  };

  const handleJumpToSlide = ({ index }: HandleJumpToSlideParamsTypes) => {
    return () => {
      if (index > maxIndex || index < 0 || carouselItemsRef.current.length <= 1 || maxIndex <= 0)
        return;

      setActiveIndex(index);
      animateCarousel(calculateShiftAmount(index));
    };
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startX = 0;
    let startY = 0;
    let diffX = 0;
    let diffY = 0;
    let shiftAmount = 0;
    let isPointerDown = false;
    let dragAxis: 'undecided' | 'horizontal' | 'vertical' = 'undecided';

    const getPoint = (event: MouseEvent | TouchEvent) => {
      if ('touches' in event) {
        return event.touches[0]
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
          : { x: 0, y: 0 };
      }

      return { x: event.clientX, y: event.clientY };
    };

    const resetToCurrentSlide = () => {
      animateCarousel(calculateShiftAmount(activeIndex));
    };

    const handleDragStart = (event: MouseEvent | TouchEvent) => {
      if (carouselItemsRef.current.length <= 1 || maxIndex <= 0) return;

      if ('touches' in event && event.touches.length > 1) return;

      const point = getPoint(event);

      startX = point.x;
      startY = point.y;
      diffX = 0;
      diffY = 0;
      dragAxis = 'undecided';
      isPointerDown = true;
      shiftAmount = calculateShiftAmount(activeIndex);

      if (carouselRef.current) {
        carouselRef.current.style.transition = 'none';
      }
    };

    const handleDragMove = (event: MouseEvent | TouchEvent) => {
      if (!isPointerDown) return;

      const point = getPoint(event);
      diffX = point.x - startX;
      diffY = point.y - startY;

      if (dragAxis === 'undecided') {
        const passedThreshold = Math.abs(diffX) > 6 || Math.abs(diffY) > 6;
        if (!passedThreshold) return;

        dragAxis = Math.abs(diffX) > Math.abs(diffY) ? 'horizontal' : 'vertical';

        if (dragAxis === 'vertical') {
          setDragging(false);
          resetToCurrentSlide();
          return;
        }

        setDragging(true);
      }

      if (dragAxis !== 'horizontal') return;

      if ('cancelable' in event && event.cancelable) {
        event.preventDefault();
      }

      if (carouselRef.current) {
        carouselRef.current.style.transform = `translate3d(${diffX - shiftAmount}px, 0, 0)`;
      }
    };

    const handleDragEnd = () => {
      if (!isPointerDown) return;

      isPointerDown = false;

      if (dragAxis !== 'horizontal') {
        dragAxis = 'undecided';
        diffX = 0;
        diffY = 0;
        setDragging(false);
        resetToCurrentSlide();
        return;
      }

      let newActiveIndex = activeIndex;

      if (diffX < -50 && activeIndex < maxIndex) {
        newActiveIndex = activeIndex + 1;
      } else if (diffX > 50 && activeIndex > 0) {
        newActiveIndex = activeIndex - 1;
      }

      dragAxis = 'undecided';
      diffX = 0;
      diffY = 0;
      setDragging(false);
      setActiveIndex(newActiveIndex);
      animateCarousel(calculateShiftAmount(newActiveIndex));
    };

    container.style.touchAction = 'pan-y';
    container.style.userSelect = 'none';

    container.addEventListener('mousedown', handleDragStart);
    container.addEventListener('touchstart', handleDragStart, { passive: true });

    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
    window.addEventListener('touchcancel', handleDragEnd);

    container.addEventListener('dragstart', (e) => e.preventDefault());

    return () => {
      container.removeEventListener('mousedown', handleDragStart);
      container.removeEventListener('touchstart', handleDragStart);

      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
      window.removeEventListener('touchcancel', handleDragEnd);
    };
  }, [activeIndex, maxIndex, animateCarousel, calculateShiftAmount]);

  React.useEffect(() => {
    if (!overrideMaxIndex) {
      calculateMaxIndex();
      window.addEventListener('resize', calculateMaxIndex);

      return () => {
        window.removeEventListener('resize', calculateMaxIndex);
      };
    }
  }, [overrideMaxIndex, calculateMaxIndex]);

  return {
    carouselRef,
    containerRef,
    carouselItemsRef,
    handleAddCarouselItemsToRef,
    activeIndex,
    handleShowNextItem,
    handleShowPreviousItem,
    handleJumpToSlide,
    maxIndex,
    dragging,
  };
};

export default useCarouselAnimation;
